import { useState, useEffect, useCallback, useRef } from "react";
import { router } from "../../../data/Services/ordersRouter.jsx";
import marketdata from "../../../data/Services/marketdata.jsx";

// Throttle interval for position updates (ms)
// Reduces DataTable re-renders which are expensive due to PrimeReact PT system
const POSITION_UPDATE_THROTTLE_MS = 500;

// Hook encapsulating all data + calculation logic for positions panel
// Returns: { positions }
// No external options currently
export const usePositionsData = () => {
  const [positions, setPositions] = useState([]);

  // Throttle state for quote-driven updates
  const pendingUpdateRef = useRef(false);
  const throttleTimeoutRef = useRef(null);
  // Track last positions to avoid unnecessary state updates
  const lastPositionsRef = useRef(null);

  const calculateSaldo = useCallback((symbol, position) => {
    if (!position) return 0;
    let { quantity = 0, price = 0, basis } = position;

    let net = 0;
    if (basis !== undefined && (basis !== 0 || quantity !== 0)) {
      net = basis;

      let absqtt = quantity;
      if (quantity < 0) absqtt *= -1;
      let pricelot = price * absqtt;

      const currPrice = marketdata?.getLastQuote?.(symbol)?.C ?? null;
      if (
        currPrice !== null &&
        typeof currPrice === "number" &&
        isFinite(currPrice)
      ) {
        let currlot = currPrice * absqtt;
        if (quantity > 0) {
          net = net + (currlot - pricelot);
        } else if (quantity < 0) {
          net = net + (pricelot - currlot);
        }
      }

      // FOQ do SERVIDOR, nunca fabricado no cliente (getFOQ é a autoridade
      // única). Indisponível → saldo null e a célula mostra "—" em vez de um
      // R$ calculado com multiplicador chutado.
      const foq = marketdata?.getFOQ?.(symbol);
      if (foq === null || foq === undefined) return null;
      net = net * foq;

      net = Number(net.toFixed(2));
    }

    return Number.isFinite(net) ? net : 0;
  }, []);

  const processPositions = useCallback(
    (accountData) => {
      if (!accountData || !accountData.positions) return [];
      const rows = [];
      const symbols = Object.keys(accountData.positions);

      // Get the master->child symbol mapping from marketdata
      const symbolTable =
        marketdata && marketdata.getObjectSymbolTable
          ? marketdata.getObjectSymbolTable()
          : new Map();

      // Filter out master symbols that have corresponding child symbols
      const filteredSymbols = symbols.filter((symbol) => {
        // Always filter out MASTER symbols - they're internal references
        if (symbol === "MASTER" || symbol.toUpperCase().includes("MASTER")) {
          return false;
        }

        // Filter out symbols ending with FUT or FUL (master symbols)
        if (symbol.endsWith("FUT") || symbol.endsWith("FUL")) {
          return false;
        }

        // If this symbol is a master symbol (exists in symbolTable keys)
        // and has a corresponding child symbol that also exists in positions,
        // then filter out the master symbol
        if (symbolTable.has(symbol)) {
          const childSymbol = symbolTable.get(symbol);
          if (childSymbol && symbols.includes(childSymbol)) {
            // This is a master symbol with existing child - filter it out
            return false;
          }
        }

        return true;
      });

      const processedSymbols = new Set();

      for (let i = 0; i < filteredSymbols.length; i++) {
        const symbol = filteredSymbols[i];

        // Avoid duplicates
        if (processedSymbols.has(symbol)) continue;
        processedSymbols.add(symbol);

        const p = accountData.positions[symbol];
        if (!p) continue;

        let currentPrice = p.price;
        const lp = marketdata?.getLastQuote?.(symbol)?.C;
        if (typeof lp === "number" && isFinite(lp)) currentPrice = lp;
        const saldo = calculateSaldo(symbol, p);
        rows.push({
          id: `pos_${symbol}`, // Stable ID based on symbol only (each symbol has one position)
          symbol,
          qtd: p.quantity,
          price: p.price,
          currentPrice,
          basis: p.basis,
          saldo,
          currency: p.currency || "BRL",
        });
      }
      return rows;
    },
    [calculateSaldo],
  );

  useEffect(() => {
    // Helper to check if positions actually changed
    const hasPositionsChanged = (newPositions) => {
      const last = lastPositionsRef.current;
      if (!last) return true;
      if (last.length !== newPositions.length) return true;
      for (let i = 0; i < newPositions.length; i++) {
        if (
          last[i].id !== newPositions[i].id ||
          last[i].qtd !== newPositions[i].qtd ||
          last[i].saldo !== newPositions[i].saldo ||
          last[i].currentPrice !== newPositions[i].currentPrice
        ) {
          return true;
        }
      }
      return false;
    };

    const handleAccountDataUpdate = (accountData) => {
      const newPositions = processPositions(accountData);
      if (hasPositionsChanged(newPositions)) {
        lastPositionsRef.current = newPositions;
        setPositions(newPositions);
      }
    };

    // Throttled update function to reduce DataTable re-renders
    const schedulePositionUpdate = () => {
      // Mark that we have a pending update
      pendingUpdateRef.current = true;

      // If we already have a timeout scheduled, let it handle the update
      if (throttleTimeoutRef.current) return;

      // Schedule the actual update
      throttleTimeoutRef.current = setTimeout(() => {
        throttleTimeoutRef.current = null;
        if (pendingUpdateRef.current) {
          pendingUpdateRef.current = false;
          // Perform the actual position update
          if (router.accountData) {
            const newPositions = processPositions(router.accountData);
            if (hasPositionsChanged(newPositions)) {
              lastPositionsRef.current = newPositions;
              setPositions(newPositions);
            }
          }
        }
      }, POSITION_UPDATE_THROTTLE_MS);
    };

    const quoteReceiver = {
      OnQuote: (quote) => {
        if (!quote || !quote.S) return;

        // Use cached positions from ref (avoids React state access on every quote)
        const posSymbols = lastPositionsRef.current;
        if (!posSymbols || posSymbols.length === 0) return;

        // Quick check if any position is affected by this quote
        const qSym = quote.S;

        // Check direct match first (fast path)
        let affected = posSymbols.some((p) => p.symbol === qSym);

        // If no direct match, check via symbol mappings (master<->child)
        if (!affected) {
          const childToMaster = marketdata.getObjectSymbolTable
            ? marketdata.getObjectSymbolTable()
            : null;
          if (childToMaster) {
            // Check if quote symbol maps to a position symbol (WINFUT -> WING26)
            if (childToMaster.has(qSym)) {
              const mapped = childToMaster.get(qSym);
              affected = posSymbols.some((p) => p.symbol === mapped);
            }
            // Check reverse: position symbol's master matches quote (WING26's master is WINFUT)
            if (!affected) {
              for (const [child, master] of childToMaster.entries()) {
                if (
                  master === qSym &&
                  posSymbols.some((p) => p.symbol === child)
                ) {
                  affected = true;
                  break;
                }
              }
            }
          }
        }

        // Only schedule update if affected (no React state access needed)
        if (affected) {
          schedulePositionUpdate();
        }
      },
    };

    const reportReceiver = {
      receiveReport: () => {
        if (router.accountData)
          setPositions(processPositions(router.accountData));
      },
    };

    // Store receiver in variable so we can unregister the same reference
    const accountDataReceiver = { receiveAccountData: handleAccountDataUpdate };

    router.registerReceiver(accountDataReceiver);
    if (marketdata && marketdata.registerReceiver)
      marketdata.registerReceiver(quoteReceiver);
    router.registerReceiver(reportReceiver);

    // Use cached account data if available (avoid redundant server request)
    // orderStore.initialize() handles the central getAccountDataAsync call
    if (router.accountData) {
      handleAccountDataUpdate(router.accountData);
    }

    return () => {
      router.unregisterReceiver(accountDataReceiver);
      if (marketdata && marketdata.unregisterReceiver)
        marketdata.unregisterReceiver(quoteReceiver);
      router.unregisterReceiver(reportReceiver);
      // Clear throttle timeout on cleanup
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }
    };
  }, [calculateSaldo, processPositions]);

  return { positions };
};
